import { Injectable, NgZone, OnDestroy } from '@angular/core';
import { take } from 'rxjs/operators';

import { Suburb } from '../../../../models/content';
import {
  SuburbAreasService,
  SuburbCouncil,
  SuburbFillResult,
  SuburbSubRegionDetail,
} from '../../../../services/suburb-areas.service';
import { apiErrorText } from './api-errors';
import { SuburbCatalogueService } from './catalogue.service';
import { ContentRecordsService } from './content-records.service';
import { inZone } from './in-zone';
import { MapEditsService } from './map-edits.service';
import { WorkspaceStateService } from './workspace-state.service';

/** How long the council search waits for typing to stop. */
const COUNCIL_SEARCH_MS = 300;

/**
 * Sub-regions (#260; plan §6.5): the open sub-region's panel (its members, its page, Fill from council areas) and the
 * changes made to a sub-region's record from the Regions panel or its own (colour, delete).
 */
@Injectable()
export class SubRegionsService implements OnDestroy {
  detail: SuburbSubRegionDetail | null = null;
  loading = false;
  error = '';
  /** Fill from council areas: the councils shown, the ones picked, and the preview. */
  councils: SuburbCouncil[] = [];
  councilsLoading = false;
  lgaLoaded = true;
  councilQuery = '';
  councilPicked = new Set<string>();
  assignUnassigned = true;
  fillPreview: SuburbFillResult | null = null;
  fillBusy = false;
  fillError = '';

  private request = 0;
  private councilRequest = 0;
  private councilTimer: ReturnType<typeof setTimeout> | undefined;

  constructor(
    private api: SuburbAreasService,
    private zone: NgZone,
    private state: WorkspaceStateService,
    private catalogue: SuburbCatalogueService,
    private records: ContentRecordsService,
    private edits: MapEditsService
  ) {}

  ngOnDestroy(): void {
    clearTimeout(this.councilTimer);
  }

  /** The sub-region whose panel is open. */
  get openId(): string | null {
    return this.state.panel?.kind === 'subregion' ? this.state.panel.id : null;
  }

  get record(): Suburb | undefined {
    return this.records.recordById(this.openId);
  }

  /** Its members changed since its page was last published here: say so beside Publish. */
  get needsPublish(): boolean {
    const id = this.openId;
    return !!id && this.record?.published === true && this.edits.isStale(id);
  }

  /** A sub-region's panel opens: nothing of the last one's is kept. */
  reset(): void {
    this.detail = null;
    this.fillPreview = null;
    this.fillError = '';
    this.councilPicked = new Set();
    this.councilQuery = '';
    this.councils = [];
  }

  /** Its members, councils and gate; `withCouncils` starts the fill with its members' councils picked. */
  load(id: string, withCouncils = false): void {
    const req = ++this.request;
    this.loading = true;
    this.error = '';
    this.api
      .subRegion(id)
      .pipe(take(1))
      .subscribe({
        next: inZone(this.zone, (d: SuburbSubRegionDetail) => {
          if (req !== this.request || this.openId !== id) return;
          this.detail = d;
          this.loading = false;
          if (withCouncils) {
            for (const c of d.councils) this.councilPicked.add(c.lgaCode);
            this.loadCouncils();
          }
          this.state.redraw();
        }),
        error: inZone(this.zone, (e: unknown) => {
          if (req !== this.request) return;
          this.loading = false;
          this.error = apiErrorText(e, 'could not load this sub-region', ['message']);
          this.state.redraw();
        }),
      });
  }

  /** The open sub-region again, if one is open (after a save that may have changed it). */
  reloadIfOpen(): void {
    if (this.openId) this.load(this.openId);
  }

  searchCouncils(): void {
    clearTimeout(this.councilTimer);
    this.councilTimer = setTimeout(
      () => this.zone.run(() => this.loadCouncils()),
      COUNCIL_SEARCH_MS
    );
  }

  /** Councils for the fill: the region's (or those matching the search), with the picked ones kept on the list. */
  loadCouncils(): void {
    const id = this.openId;
    if (!id) return;
    const req = ++this.councilRequest;
    this.councilsLoading = true;
    this.api
      .councilAreas(id, this.councilQuery)
      .pipe(take(1))
      .subscribe({
        next: inZone(this.zone, (r: { lgaLoaded: boolean; councils: SuburbCouncil[] }) => {
          if (req !== this.councilRequest) return;
          this.councilsLoading = false;
          this.lgaLoaded = r.lgaLoaded;
          const shown = new Set(r.councils.map((c) => c.lgaCode));
          const kept = this.councils.filter(
            (c) => this.councilPicked.has(c.lgaCode) && !shown.has(c.lgaCode)
          );
          this.councils = [...kept, ...r.councils];
          this.state.redraw();
        }),
        error: inZone(this.zone, (e: unknown) => {
          if (req !== this.councilRequest) return;
          this.councilsLoading = false;
          this.fillError = apiErrorText(e, 'could not load the council areas', ['message']);
          this.state.redraw();
        }),
      });
  }

  toggleCouncil(code: string, on: boolean): void {
    if (on) this.councilPicked.add(code);
    else this.councilPicked.delete(code);
    this.fillPreview = null;
  }

  /** A fill option changed: the preview no longer says what Apply would do. */
  clearPreview(): void {
    this.fillPreview = null;
  }

  /** Preview: what Apply would do, nothing saved. */
  previewFill(): void {
    if (!this.openId || !this.councilPicked.size) return;
    this.fillBusy = true;
    this.fillError = '';
    this.api
      .fill(this.fillBody(true))
      .pipe(take(1))
      .subscribe({
        next: inZone(this.zone, (r: SuburbFillResult) => {
          this.fillBusy = false;
          this.fillPreview = r;
          this.state.redraw();
        }),
        error: inZone(this.zone, (e: unknown) => {
          this.fillBusy = false;
          this.fillFailed(e);
          this.state.redraw();
        }),
      });
  }

  /** Apply: one batch on the server, one undo step here; queued behind any map save still going. */
  applyFill(): void {
    const id = this.openId;
    if (!id || !this.councilPicked.size || this.state.readOnly) return;
    const name = this.catalogue.subRegionName(id);
    const body = this.fillBody(false);
    this.fillBusy = true;
    this.fillError = '';
    this.edits.enqueue(
      async () => {
        try {
          const r = await this.api.fill(body).toPromise();
          this.filled(id, name, r);
        } catch (e) {
          this.fillFailed(e);
        }
      },
      () => {
        this.fillBusy = false;
        if (this.openId !== id) return;
        this.load(id);
        this.loadCouncils();
      }
    );
  }

  /** Why a sub-region can't be deleted now, or null when it can (no suburbs in it, not on the website). */
  deleteBlock(id: string): string | null {
    const n = this.state.memberCounts.get(id) ?? 0;
    if (n) return `It has ${n} suburb${n === 1 ? '' : 's'} in it: take them out first.`;
    if (this.records.recordById(id)?.published !== false)
      return "It's on the website: un-publish its page first.";
    return null;
  }

  /** Delete a sub-region and its (unpublished) page, once the person confirms. */
  delete(id: string): void {
    const record = this.records.recordById(id);
    if (!record || this.deleteBlock(id) || this.state.readOnly) return;
    const designed = this.records.designedPage(record);
    const copy = `Delete the sub-region "${record.name}" and its page? It has no suburbs and isn't on the website, so nothing there changes.`;
    this.records.removeWithPage(
      record,
      designed?.published !== true ? designed : undefined,
      { title: 'Delete sub-region', copy, label: 'Delete' },
      () => {
        this.state.setStatus(`Deleted the sub-region ${record.name}.`);
        if (this.state.panel?.kind === 'subregion') this.state.panel = { kind: 'regions' };
        void this.catalogue.reload();
      }
    );
  }

  /** The sub-region's outline colour (1–8), saved on its record. */
  setColour(id: string, slot: number): void {
    const record = this.records.recordById(id);
    if (!record || this.state.readOnly || record.mapColour === slot) return;
    this.records.writeFresh(
      record,
      (fresh) => ({ ...fresh, mapColour: slot }),
      (next) => {
        this.state.setStatus(`${next.name}'s colour changed.`);
        void this.catalogue.reload();
      }
    );
  }

  private filled(id: string, name: string, r: SuburbFillResult): void {
    this.state.engine.applyRows(r.rows);
    if (r.batchId) this.edits.pushUndo(r.batchId, `fill ${name} from council areas`);
    if (r.summary.joined) this.edits.markStale(id);
    this.fillPreview = null;
    const s = r.summary;
    const also = s.assigned
      ? ` (${s.assigned} also put in ${this.catalogue.regionName(this.catalogue.subRegionRef(id)?.regionId)})`
      : '';
    this.state.setStatus(
      `Filled ${name}: ${s.joined} joined${also}, ${s.already} already in it, ${s.skipped} skipped. Cmd+Z undoes.`
    );
  }

  private fillBody(dryRun: boolean): {
    subRegionId: string;
    lgaCodes: string[];
    assignUnassigned: boolean;
    dryRun: boolean;
  } {
    return {
      subRegionId: this.openId ?? '',
      lgaCodes: [...this.councilPicked],
      assignUnassigned: this.assignUnassigned,
      dryRun,
    };
  }

  private fillFailed(e: unknown): void {
    this.fillError = apiErrorText(e, 'the fill failed', ['message', 'thrown']);
  }
}
